// The bear's game (sprint 35 item 3): paws swipe down into a one-bit box; get out from under the warning shadow.
// Shared by the page and the server replay. Integer pixels in a 152 x 88 box, paws are half-open rects, no floats and no
// Math.pow/sin/exp; all randomness is read from the seed bytes (one per step), never from a clock.
export const id = 'bear';
export const STEPS = 105;
export const NAME = "The bear's game";

// The tuning numbers (the designer's), all integers:
//  W:   steps between a single's warning shadow and its slam (a double warns W + 1, the big one W + 2);
//  paw: width in px of a single paw; V: the doorkeeper's speed in px a step on each axis.
export const TUNING = { W: 7, paw: 18, V: 8 };

const BOX = { w: 152, h: 88 };
const HALF = { w: 5, h: 6 };
const MARKS = 3;
const MERCY = 6;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// [warnStep, kind, extra lead]: the teaching swipe (warns at 6, slams at 14, whatever W is),four singles, three doubles (one is a rake), the big one
const plan = () => [[6, 'teach', 8 - TUNING.W], [15, 'single', 0], [24, 'single', 0], [33, 'single', 0], [42, 'single', 0], [53, 'double', 1], [65, 'double', 1], [77, 'double', 1], [90, 'big', 2]];

function rectsFor(kind, warn, me, seed) {
  const col = (x0, x1) => ({ x0: clamp(x0, 0, BOX.w), x1: clamp(x1, 0, BOX.w), y0: 0, y1: BOX.h });
  // two full-depth columns leaving a gap `half` either side of a centre `near` + (0..spread-1) px from the doorkeeper, on a seeded side
  const gap = (half, near, spread) => {
    const c = clamp(me.x + (seed[warn + 1] & 1 ? 1 : -1) * (near + (seed[warn + 2] % spread)), half, BOX.w - half);
    return [col(0, c - half), col(c + half, BOX.w)];
  };
  if (kind === 'teach') return [col(me.x < BOX.w / 2 ? 110 : 20, (me.x < BOX.w / 2 ? 110 : 20) + TUNING.paw)];
  if (kind === 'single') return [col(me.x - TUNING.paw / 2, me.x + TUNING.paw / 2)];
  if (kind === 'double') return seed[53] % 3 === (warn - 53) / 12 ? [{ x0: 0, x1: BOX.w, y0: 0, y1: BOX.h / 2 }] : gap(13, 16, 17);
  return gap(10, 24, 17);
}

// The paws aim at where the doorkeeper has been lately (`avg`: each step it closes half the gap to him, in integers), not only at
// the spot he stands on this step: a flailer who swings corner to corner is no longer missed by luck, while a player who holds
// or steps aside and comes back is aimed at about where he stands.
const aimX = s => s.avg;

export function init(seed) {
  return { t: 0, seed, me: { x: BOX.w / 2, y: BOX.h / 2 }, threats: [], marks: MARKS, mercyUntil: -1, hitStep: -1, avg: BOX.w / 2, over: false, out: false };
}

// marks (left) and hitStep (the step the last mark was lost, -1 for none) are for the frame (client/bear-game-view.js); the
// frozen players do not read them.
export function observe(s) {
  return { t: s.t, box: BOX, half: HALF, me: { x: s.me.x, y: s.me.y }, V: TUNING.V, threats: s.threats.map(({ warnStep, slamStep, rects }) => ({ warnStep, slamStep, rects })), marks: s.marks, hitStep: s.hitStep };
}

export function step(s, input) {
  if (s.over) return s;
  const { V } = TUNING;
  const [xb, yb] = Array.isArray(input) ? input : [0, 0];
  if (xb > 0 && yb > 0) {
    s.me.x = clamp(s.me.x + clamp(xb - 1 - s.me.x, -V, V), HALF.w, BOX.w - HALF.w);
    s.me.y = clamp(s.me.y + clamp(yb - 1 - s.me.y, -V, V), HALF.h, BOX.h - HALF.h);
  }
  s.avg += Math.trunc((s.me.x - s.avg) / 2);
  const hit = s.threats.some(th => s.t >= th.slamStep && s.t < th.slamStep + 3 && th.rects.some(r => s.me.x + HALF.w > r.x0 && s.me.x - HALF.w < r.x1 && s.me.y + HALF.h > r.y0 && s.me.y - HALF.h < r.y1));
  if (hit && s.t > s.mercyUntil) {
    s.marks -= 1;
    s.mercyUntil = s.t + MERCY;
    s.hitStep = s.t;
    if (s.marks <= 0) { s.out = true; s.over = true; }
  }
  s.t += 1;
  if (!s.over && s.t >= STEPS) s.over = true;
  for (const [warnStep, kind, extra] of plan()) if (warnStep === s.t) s.threats.push({ warnStep, slamStep: warnStep + TUNING.W + extra, rects: rectsFor(kind, warnStep, kind === 'teach' ? s.me : { x: aimX(s) }, s.seed) });
  return s;
}

export const result = s => ({ over: s.over, steps: s.t, reachedEnd: s.over && !s.out, clean: s.marks === MARKS });
